import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabase';
import { loadSkills, getCurrentSkills } from '../Growth/skillData';

/**
 * useGrowthData — fetches the current user's skill_scores plus the
 * timestamp of their most recent interview rep (skill_pulse_conversations).
 *
 * Returns:
 *   {
 *     userId,
 *     allRows,        // raw skill_scores rows, recorded_at DESC
 *     currentSkills,  // one row per skill_name (latest)
 *     lastInterviewAt,// Date | null
 *     loading,
 *     error,
 *     refresh,
 *   }
 */
export default function useGrowthData() {
  const [userId, setUserId] = useState(null);
  const [allRows, setAllRows] = useState([]);
  const [currentSkills, setCurrentSkills] = useState([]);
  const [lastInterviewAt, setLastInterviewAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData?.user?.id || null;
      setUserId(uid);
      if (!uid) {
        setAllRows([]);
        setCurrentSkills([]);
        setLastInterviewAt(null);
        return;
      }

      const [skillsRes, interviewRes] = await Promise.allSettled([
        loadSkills(uid),
        supabase
          .from('skill_pulse_conversations')
          .select('created_at')
          .eq('user_id', uid)
          .order('created_at', { ascending: false })
          .limit(1),
      ]);

      const rows = skillsRes.status === 'fulfilled' ? skillsRes.value : [];
      setAllRows(rows);
      setCurrentSkills(getCurrentSkills(rows));

      if (interviewRes.status === 'fulfilled') {
        const row = interviewRes.value?.data?.[0];
        setLastInterviewAt(row ? new Date(row.created_at) : null);
      } else {
        setLastInterviewAt(null);
      }

      const firstReject = [skillsRes, interviewRes].find((r) => r.status === 'rejected');
      if (firstReject) setError(firstReject.reason?.message || 'Partial load failure');
    } catch (e) {
      setError(e?.message || 'Failed to load growth data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    userId,
    allRows,
    currentSkills,
    lastInterviewAt,
    loading,
    error,
    refresh: load,
  };
}
